// src/components/satellite/ClubPitchMap.jsx - Fixed responsive layout with proper mobile burger menu
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings } from 'lucide-react';
import { auth, db } from '../../utils/firebase';
import { doc, getDoc, onSnapshot, collection, getDocs } from 'firebase/firestore';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import logo from '../../assets/images/logo2.PNG';
import { AdminOnly, useUserRole } from '../../contexts/UserContext';

// Feedback topic options
const feedbackTopics = [
  'Bug Report',
  'Feature Request',
  'User Interface Issue',
  'Performance Issue',
  'Data/Sync Problem',
  'Login/Authentication Issue',
  'Settings Issue',
  'Team Configuration Issue',
  'Pitch Configuration Issue',
  'Import/Export Issue',
  'General Feedback',
  'Other'
];

const ClubPitchMap = ({ 
  onPitchClick 
}) => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  
  // Mobile state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  
  // States for data loading
  const [satelliteConfig, setSatelliteConfig] = useState(null);
  const [pitchNames, setPitchNames] = useState({});
  const [clubId, setClubId] = useState(null);
  const [clubName, setClubName] = useState('');
  const [branding, setBranding] = useState(null);
  const [clubData, setClubData] = useState(null); // For subscription status
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Multi-facility state
  const [facilities, setFacilities] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState(null);
  const [primaryFacilityId, setPrimaryFacilityId] = useState(null);
  
  // User states
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);

  // Feedback modal states
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({
    from: '',
    topic: '',
    content: '',
    url: ''
  });
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  // Feedback modal functions
  const openFeedbackModal = () => {
    setFeedbackForm({
      from: user?.email || '',
      topic: '',
      content: '',
      url: window.location.href
    });
    setShowFeedbackModal(true);
    setIsMobileSidebarOpen(false);
  };

  const closeFeedbackModal = () => {
    setShowFeedbackModal(false);
    setFeedbackForm({
      from: '',
      topic: '',
      content: '',
      url: ''
    });
  };

  const handleFeedbackInputChange = (field, value) => {
    setFeedbackForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const submitFeedback = async () => {
    if (!feedbackForm.from || !feedbackForm.topic || !feedbackForm.content) {
      alert('Please fill in all fields');
      return;
    }

    setIsSubmittingFeedback(true);

    try {
      const formData = new FormData();
      formData.append('form-name', 'feedback-form');
      formData.append('email', feedbackForm.from);
      formData.append('club', clubName || 'Unknown');
      formData.append('topic', feedbackForm.topic);
      formData.append('message', feedbackForm.content);
      formData.append('url', feedbackForm.url);

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData).toString()
      });

      if (response.ok) {
        alert('Thank you! Your feedback has been sent successfully. We\'ll review it soon.');
        closeFeedbackModal();
      } else {
        throw new Error(`Server responded with status: ${response.status}`);
      }
    } catch (error) {
      console.error('Netlify Forms submission failed:', error);
      alert('Direct submission failed. Opening your email client as backup...');
      
      const subject = `PitcHero Feedback: ${feedbackForm.topic}`;
      const body = `From: ${feedbackForm.from}
Club: ${clubName || 'Unknown'}
Topic: ${feedbackForm.topic}
URL: ${feedbackForm.url}

Message:
${feedbackForm.content}

---
Sent from PitcHero Feedback Form`;

      const mailtoLink = `mailto:giannini000@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      
      try {
        window.location.href = mailtoLink;
        setTimeout(() => {
          alert('Your email client should have opened.');
        }, 1000);
        closeFeedbackModal();
      } catch (mailtoError) {
        console.error('Mailto also failed:', mailtoError);
        closeFeedbackModal();
      }
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  // Utility functions for rotated rectangles
  const rotatePoint = (point, center, angle) => {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const dx = point.x - center.x;
    const dy = point.y - center.y;
    
    return {
      x: center.x + dx * cos - dy * sin,
      y: center.y + dx * sin + dy * cos
    };
  };

  const getRotatedRectangleCorners = (boundary) => {
    if (boundary.type === 'normal' || !boundary.type) {
      return [
        { x: boundary.boundaries.x1, y: boundary.boundaries.y1 },
        { x: boundary.boundaries.x2, y: boundary.boundaries.y1 },
        { x: boundary.boundaries.x2, y: boundary.boundaries.y2 },
        { x: boundary.boundaries.x1, y: boundary.boundaries.y2 }
      ];
    } else {
      const center = { x: boundary.centerX, y: boundary.centerY };
      const halfWidth = boundary.width / 2;
      const halfHeight = boundary.height / 2;
      
      const corners = [
        { x: center.x - halfWidth, y: center.y - halfHeight },
        { x: center.x + halfWidth, y: center.y - halfHeight },
        { x: center.x + halfWidth, y: center.y + halfHeight },
        { x: center.x - halfWidth, y: center.y + halfHeight }
      ];
      
      return corners.map(corner => rotatePoint(corner, center, boundary.rotation));
    }
  };

  const isPointInRotatedRectangle = (point, boundary) => {
    const corners = getRotatedRectangleCorners(boundary);
    
    let inside = false;
    for (let i = 0, j = corners.length - 1; i < corners.length; j = i++) {
      if (((corners[i].y > point.y) !== (corners[j].y > point.y)) &&
          (point.x < (corners[j].x - corners[i].x) * (point.y - corners[i].y) / (corners[j].y - corners[i].y) + corners[i].x)) {
        inside = !inside;
      }
    }
    return inside;
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
    setIsMobileSidebarOpen(false);
  };

  // Handle home navigation
  const handleHomeClick = () => {
    navigate('/menu');
    setIsMobileSidebarOpen(false);
  };

  // Handle navigation with mobile menu close
  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileSidebarOpen(false);
  };

  // Load user and club data
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            setUserRole(userData.role || 'user');
            
            if (userData.clubId) {
              setClubId(userData.clubId);
            } else {
              setError('No club associated with this user');
              setLoading(false);
            }
          } else {
            setError('User data not found');
            setLoading(false);
          }
        } catch (err) {
          console.error('Error fetching user data:', err);
          setError('Failed to load user data');
          setLoading(false);
        }
      } else {
        setError('User not authenticated');
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Load facilities when clubId is available
  useEffect(() => {
    const loadFacilities = async () => {
      if (!clubId) return;
      
      try {
        console.log('📦 [ClubPitchMap] Loading facilities for club:', clubId);
        const facilitiesRef = collection(db, 'clubs', clubId, 'facilities');
        const facilitiesSnap = await getDocs(facilitiesRef);
        
        const facilitiesData = [];
        facilitiesSnap.forEach((doc) => {
          facilitiesData.push({ id: doc.id, ...doc.data() });
        });
        
        // Sort: primary first, then alphabetically
        facilitiesData.sort((a, b) => {
          if (a.isPrimary) return -1;
          if (b.isPrimary) return 1;
          return (a.name || '').localeCompare(b.name || '');
        });
        
        setFacilities(facilitiesData);
        console.log('✅ [ClubPitchMap] Loaded facilities:', facilitiesData.length);
        
        // Get primary facility ID from club doc
        const clubDoc = await getDoc(doc(db, 'clubs', clubId));
        if (clubDoc.exists()) {
          const primaryId = clubDoc.data().primaryFacilityId;
          setPrimaryFacilityId(primaryId);
          
          // Auto-select primary facility, or first facility if no primary set
          if (facilitiesData.length > 0) {
            const defaultFacility = primaryId 
              ? facilitiesData.find(f => f.id === primaryId) || facilitiesData[0]
              : facilitiesData[0];
            setSelectedFacilityId(defaultFacility.id);
            console.log('🎯 [ClubPitchMap] Selected facility:', defaultFacility.name);
          }
        }
        
        // Load branding from settings
        const settingsRef = doc(db, 'clubs', clubId, 'settings', 'general');
        const settingsDoc = await getDoc(settingsRef);
        if (settingsDoc.exists() && settingsDoc.data().branding) {
          setBranding(settingsDoc.data().branding);
        }
      } catch (err) {
        console.error('❌ [ClubPitchMap] Error loading facilities:', err);
      }
    };
    
    loadFacilities();
  }, [clubId]);

  // Real-time satellite configuration listener
  useEffect(() => {
    if (!clubId) {
      return;
    }

    // First, always get club name from club doc
    const clubRef = doc(db, 'clubs', clubId);
    
    // If we have a selected facility, load from facility doc
    if (selectedFacilityId) {
      console.log('📦 [ClubPitchMap] Loading satellite config from facility:', selectedFacilityId);
      
      const facilityRef = doc(db, 'clubs', clubId, 'facilities', selectedFacilityId);
      const unsubscribeFacility = onSnapshot(facilityRef, async (facilityDoc) => {
        // Get club name
        const clubDocSnap = await getDoc(clubRef);
        if (clubDocSnap.exists()) {
          setClubName(clubDocSnap.data().name || 'Club');
        }
        
        if (facilityDoc.exists()) {
          const facilityData = facilityDoc.data();
          
          // Build satelliteConfig from facility
          const pitchesData = facilityData.pitches || [];
          
          // Check multiple locations for image dimensions
          // Priority: facilityData root level > satelliteConfig > defaults
          const imageWidth = facilityData.imageWidth || 
                           facilityData.satelliteConfig?.imageWidth || 
                           2048;  // Updated default to match capture
          const imageHeight = facilityData.imageHeight || 
                            facilityData.satelliteConfig?.imageHeight || 
                            1536;  // Updated default to match capture
          
          const newSatelliteConfig = {
            imageUrl: facilityData.satelliteImageUrl || facilityData.satelliteImage || null,
            imageWidth: imageWidth,
            imageHeight: imageHeight,
            pitches: pitchesData,
            pitchBoundaries: pitchesData  // Alias for compatibility
          };
          
          console.log('🛰️ [ClubPitchMap] Facility satellite config:', {
            hasImage: !!newSatelliteConfig.imageUrl,
            pitchCount: newSatelliteConfig.pitches.length,
            dimensions: `${newSatelliteConfig.imageWidth}x${newSatelliteConfig.imageHeight}`,
            pitchNumbers: pitchesData.map(p => p.pitchNumber)
          });
          
          // Check if satellite image changed
          if (newSatelliteConfig?.imageUrl !== satelliteConfig?.imageUrl) {
            setImageLoaded(false);
          }
          
          setSatelliteConfig(newSatelliteConfig);
        } else {
          console.warn('⚠️ [ClubPitchMap] Facility not found:', selectedFacilityId);
          setSatelliteConfig(null);
        }
        setLoading(false);
      }, (error) => {
        console.error('Error in facility config listener:', error);
        setError('Failed to load facility configuration');
        setLoading(false);
      });
      
      // Load pitch names from settings/general (where custom names are stored)
      const loadPitchNames = async () => {
        try {
          const settingsRef = doc(db, 'clubs', clubId, 'settings', 'general');
          const settingsDoc = await getDoc(settingsRef);
          
          if (settingsDoc.exists()) {
            const settingsData = settingsDoc.data();
            console.log('📝 [ClubPitchMap] Loaded pitchNames from settings:', settingsData.pitchNames);
            setPitchNames(settingsData.pitchNames || {});
          } else {
            setPitchNames({});
          }
        } catch (settingsError) {
          console.error('Error loading pitch names:', settingsError);
          setPitchNames({});
        }
      };
      
      loadPitchNames();
      
      return () => {
        unsubscribeFacility();
      };
    }
    
    // Fallback: Load from club document (legacy mode)
    console.log('📦 [ClubPitchMap] Loading satellite config from club document (legacy)');
    const unsubscribeSatellite = onSnapshot(clubRef, (doc) => {
      if (doc.exists()) {
        const clubData = doc.data();
        setClubData(clubData); // Store for subscription status
        setClubName(clubData.name || 'Club');
        
        // Build satelliteConfig from flat structure
        const pitchesData = clubData.pitches || [];
        
        // Check multiple locations for dimensions
        const imageWidth = clubData.imageWidth || 
                         clubData.satelliteConfig?.imageWidth || 
                         2048;
        const imageHeight = clubData.imageHeight || 
                          clubData.satelliteConfig?.imageHeight || 
                          1536;
        
        const newSatelliteConfig = {
          imageUrl: clubData.satelliteImage || clubData.satelliteImageUrl || null,
          imageWidth: imageWidth,
          imageHeight: imageHeight,
          pitches: pitchesData,
          pitchBoundaries: pitchesData  // Alias for compatibility
        };
        
        console.log('🛰️ [ClubPitchMap] Club satellite config:', {
          hasImage: !!newSatelliteConfig.imageUrl,
          pitchCount: newSatelliteConfig.pitches.length,
          dimensions: `${newSatelliteConfig.imageWidth}x${newSatelliteConfig.imageHeight}`,
          pitchNumbers: pitchesData.map(p => p.pitchNumber)
        });
        
        // Check if satellite image changed
        if (newSatelliteConfig?.imageUrl !== satelliteConfig?.imageUrl) {
          setImageLoaded(false);
        }
        
        setSatelliteConfig(newSatelliteConfig);
        
      } else {
        setError('Club not found');
      }
      setLoading(false);
    }, (error) => {
      console.error('Error in satellite config listener:', error);
      setError('Failed to load satellite configuration');
      setLoading(false);
    });

    // Load pitch names
    const loadPitchNames = async () => {
      try {
        const settingsRef = doc(db, 'clubs', clubId, 'settings', 'general');
        const settingsDoc = await getDoc(settingsRef);
        
        if (settingsDoc.exists()) {
          const settingsData = settingsDoc.data();
          
          if (settingsData.pitchNames) {
            setPitchNames(settingsData.pitchNames);
          } else {
            setPitchNames({});
          }
        } else {
          setPitchNames({});
        }
      } catch (settingsError) {
        console.error('Error loading pitch names:', settingsError);
        setPitchNames({});
      }
    };

    loadPitchNames();

    return () => {
      unsubscribeSatellite();
    };
  }, [clubId, selectedFacilityId]);

  // Helper function to get pitch display name
  // Normalize pitch ID to consistent format: pitch-{number}
  const normalizePitchId = (pitchId) => {
    if (!pitchId) return '';
    const id = String(pitchId);
    // If it already has the pitch- format, return as is
    if (id.startsWith('pitch-')) return id;
    // If it's just "pitch1" format, convert to "pitch-1"
    if (id.startsWith('pitch')) return id.replace('pitch', 'pitch-');
    // If it's just a number, add pitch- prefix
    return `pitch-${id}`;
  };

  const getPitchDisplayName = (pitchNumber, pitchNamesData = pitchNames) => {
    const normalizedId = normalizePitchId(pitchNumber);
    const possibleKeys = [
      normalizedId,
      `pitch${pitchNumber}`,
      `pitch-${pitchNumber}`,
      `Pitch ${pitchNumber}`,
      `Pitch-${pitchNumber}`,
      pitchNumber.toString(),
    ];
    
    for (const key of possibleKeys) {
      if (pitchNamesData && pitchNamesData[key]) {
        return pitchNamesData[key];
      }
    }
    
    return `Pitch ${pitchNumber}`;
  };

  // Calculate canvas size - larger for PC
  const calculateCanvasSize = (imgWidth, imgHeight) => {
    if (!imgWidth || !imgHeight || imgWidth <= 0 || imgHeight <= 0) {
      return { width: 1000, height: 700 };
    }

    const maxWidth = window.innerWidth < 768 ? window.innerWidth - 40 : 1000;
    const maxHeight = window.innerWidth < 768 ? 400 : 700;
    const aspectRatio = imgWidth / imgHeight;
    
    let width = maxWidth;
    let height = width / aspectRatio;
    
    if (height > maxHeight) {
      height = maxHeight;
      width = height * aspectRatio;
    }
    
    if (width < 400) width = 400;
    if (height < 300) height = 300;
    
    return { width: Math.floor(width), height: Math.floor(height) };
  };

  // Handle image load
  const handleImageLoad = () => {
    if (imageRef.current && satelliteConfig) {
      const imgWidth = satelliteConfig.imageWidth || imageRef.current.naturalWidth;
      const imgHeight = satelliteConfig.imageHeight || imageRef.current.naturalHeight;
      
      const size = calculateCanvasSize(imgWidth, imgHeight);
      setCanvasSize(size);
      setImageLoaded(true);
    }
  };

  // Handle image error
  const handleImageError = (e) => {
    console.error('Image failed to load:', e);
    setError('Failed to load satellite image due to CORS restrictions');
  };

  // Draw the canvas
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    
    if (!canvas || !image || !imageLoaded || !canvasSize.width || !canvasSize.height) {
      return;
    }

    try {
      const ctx = canvas.getContext('2d');
      
      canvas.width = canvasSize.width;
      canvas.height = canvasSize.height;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

      const scaleX = canvas.width / (satelliteConfig.imageWidth || image.naturalWidth);
      const scaleY = canvas.height / (satelliteConfig.imageHeight || image.naturalHeight);

      if (satelliteConfig.pitches && satelliteConfig.pitches.length > 0) {
        satelliteConfig.pitches.forEach((pitch, index) => {
          drawPitchBoundary(ctx, pitch, scaleX, scaleY, index);
        });
      }
      
      canvas.style.display = 'block';
      canvas.style.visibility = 'visible';
      
    } catch (error) {
      console.error('Error drawing canvas:', error);
      setError('Failed to draw map');
    }
  }, [imageLoaded, satelliteConfig, canvasSize]);

  // Draw pitch boundary
  const drawPitchBoundary = (ctx, pitch, scaleX, scaleY, index) => {
    try {
      console.log('[ClubPitchMap] Drawing pitch:', index, pitch);
      
      // Handle both flat structure and nested boundaries structure
      const pitchData = pitch.boundaries ? pitch.boundaries : pitch;
      
      const centerX = pitchData.centerX * scaleX;
      const centerY = pitchData.centerY * scaleY;
      
      // If pitch has corners (new format), draw as polygon
      if (pitchData.corners && pitchData.corners.length === 4) {
        ctx.fillStyle = 'rgba(59, 130, 246, 0.35)';
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 2;
        
        ctx.beginPath();
        ctx.moveTo(pitchData.corners[0].x * scaleX, pitchData.corners[0].y * scaleY);
        for (let i = 1; i < 4; i++) {
          ctx.lineTo(pitchData.corners[i].x * scaleX, pitchData.corners[i].y * scaleY);
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      } else {
        // Legacy format: draw as rotated rectangle
        const width = pitchData.width * scaleX;
        const height = pitchData.height * scaleY;
        const rotation = pitchData.rotation || 0;
        
        ctx.save();
        ctx.translate(centerX, centerY);
        ctx.rotate(rotation);
        
        // Draw filled rectangle
        ctx.fillStyle = 'rgba(59, 130, 246, 0.35)';
        ctx.fillRect(-width / 2, -height / 2, width, height);
        
        // Draw border
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 2;
        ctx.strokeRect(-width / 2, -height / 2, width, height);
        
        // Draw label (counter-rotate so text stays upright)
        ctx.rotate(-rotation);
      }
      
      // Get display name - PRIORITY: custom name from settings > pitch.name from satellite > fallback
      let displayName = null;
      
      // Get pitch number for key lookups
      const pitchNum = pitch.pitchNumber || (index + 1);
      
      // Check multiple key formats (consistent with legend and mobile)
      const possibleKeys = [
        `pitch-${pitchNum}`,
        pitch.id,
        `pitch${pitchNum}`,
        `Pitch ${pitchNum}`,
        `Pitch-${pitchNum}`,
        pitchNum.toString(),
        pitchNum
      ].filter(Boolean);
      
      // PRIORITY 1: Check pitchNames for custom name (try all possible keys)
      for (const key of possibleKeys) {
        if (pitchNames && pitchNames[key] && pitchNames[key].trim && pitchNames[key].trim() !== '') {
          displayName = pitchNames[key];
          break;
        }
      }
      
      // PRIORITY 2: Use pitch.name from facility document (default from satellite drawing)
      if (!displayName && pitch.name && pitch.name.trim() !== '') {
        displayName = pitch.name;
      }
      
      // PRIORITY 3: Fallback to "Pitch X"
      if (!displayName) {
        displayName = `Pitch ${pitchNum}`;
      }
      
      // Draw text with white fill and black stroke for maximum contrast
      ctx.font = 'bold 20px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      // Black stroke (outline) for contrast
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 4;
      ctx.strokeText(displayName, centerX, centerY);
      
      // White fill
      ctx.fillStyle = '#ffffff';
      ctx.fillText(displayName, centerX, centerY);
    } catch (error) {
      console.error('Error drawing pitch boundary:', error);
    }
  };

  // Trigger redraw when needed
  useEffect(() => {
    if (imageLoaded && canvasRef.current && canvasSize.width > 0 && canvasSize.height > 0) {
      setTimeout(() => {
        drawCanvas();
      }, 100);
    }
  }, [imageLoaded, satelliteConfig, drawCanvas, canvasSize]);

  // Handle canvas click
  const handleCanvasClick = (e) => {
    if (!satelliteConfig?.pitches || !canvasSize.width || !canvasSize.height) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const clickX = ((e.clientX - rect.left) / canvasSize.width) * (satelliteConfig.imageWidth || imageRef.current?.naturalWidth || 1);
    const clickY = ((e.clientY - rect.top) / canvasSize.height) * (satelliteConfig.imageHeight || imageRef.current?.naturalHeight || 1);

    const clickedPitch = satelliteConfig.pitches.find(pitch => {
      // Handle both flat and nested boundaries structure
      const pitchData = pitch.boundaries ? pitch.boundaries : pitch;
      
      // Check if click is within rotated rectangle
      const dx = clickX - pitchData.centerX;
      const dy = clickY - pitchData.centerY;
      
      // Rotate click point back by -rotation
      const rotation = pitchData.rotation || 0;
      const cos = Math.cos(-rotation);
      const sin = Math.sin(-rotation);
      const localX = dx * cos - dy * sin;
      const localY = dx * sin + dy * cos;
      
      // Check if within rectangle bounds
      return Math.abs(localX) <= pitchData.width / 2 && Math.abs(localY) <= pitchData.height / 2;
    });

    if (clickedPitch) {
      if (onPitchClick) {
        onPitchClick(clickedPitch);
      } else {
        // Extract pitch number from pitch.id (format: "pitch-1", "pitch-2", etc.)
        let pitchNumber;
        if (clickedPitch.pitchNumber) {
          pitchNumber = clickedPitch.pitchNumber;
        } else if (clickedPitch.id) {
          // Extract number from "pitch-1" format
          pitchNumber = clickedPitch.id.replace('pitch-', '').replace('pitch', '');
        } else {
          pitchNumber = 1;
        }
        const pitchId = `pitch-${pitchNumber}`;
        console.log('[ClubPitchMap] Navigating to:', pitchId);
        navigate(`/allocator/${pitchId}`);
      }
      setIsMobileSidebarOpen(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        flexDirection: 'column',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{
          animation: 'spin 1s linear infinite',
          width: '50px',
          height: '50px',
          border: '4px solid #e5e7eb',
          borderTop: '4px solid #3b82f6',
          borderRadius: '50%',
          marginBottom: '16px'
        }}></div>
        <h3>Loading Satellite Map</h3>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        flexDirection: 'column',
        backgroundColor: '#fef2f2',
        padding: '20px'
      }}>
        <Settings size={64} color="#ef4444" style={{ marginBottom: '16px' }} />
        <h3>Error Loading Map</h3>
        <p style={{ color: '#7f1d1d', textAlign: 'center', maxWidth: '600px' }}>
          {error}
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: '16px',
            padding: '12px 24px',
            backgroundColor: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Reload Page
        </button>
      </div>
    );
  }

  // No satellite config
  if (!satelliteConfig || !satelliteConfig.imageUrl) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        flexDirection: 'column',
        backgroundColor: '#fff3cd',
        padding: '20px'
      }}>
        <Settings size={64} color="#856404" style={{ marginBottom: '16px' }} />
        <h3>No Satellite Image</h3>
        <p style={{ color: '#856404', textAlign: 'center' }}>
          No satellite image has been configured for this facility yet.
        </p>
      </div>
    );
  }

  // Main render with responsive design
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f9fafb',
      fontFamily: 'system-ui, sans-serif'
    }}>
      {/* CSS for mobile responsiveness */}
      <style>
        {`
          @media (max-width: 1024px) {
            .desktop-layout {
              display: none !important;
            }
            
            .mobile-layout {
              display: flex !important;
            }
          }

          @media (min-width: 1025px) {
            .desktop-layout {
              display: flex !important;
            }
            
            .mobile-layout {
              display: none !important;
            }
          }
        `}
      </style>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '16px',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#1f2937',
                margin: 0
              }}>
                Feedback / Report a Bug
              </h3>
              <button
                onClick={closeFeedbackModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Email Field */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  From (Your Email)
                </label>
                <input
                  type="email"
                  value={feedbackForm.from}
                  onChange={(e) => handleFeedbackInputChange('from', e.target.value)}
                  placeholder="your.email@example.com"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Topic Field */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  Topic
                </label>
                <select
                  value={feedbackForm.topic}
                  onChange={(e) => handleFeedbackInputChange('topic', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select a topic...</option>
                  {feedbackTopics.map((topic, index) => (
                    <option key={index} value={topic}>
                      {topic}
                    </option>
                  ))}
                </select>
              </div>

              {/* URL Field */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  Page URL
                </label>
                <input
                  type="text"
                  value={feedbackForm.url}
                  onChange={(e) => handleFeedbackInputChange('url', e.target.value)}
                  placeholder="The page where you encountered this issue"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: '#f9fafb',
                    fontFamily: 'monospace',
                    color: '#6b7280'
                  }}
                />
                <p style={{
                  fontSize: '12px',
                  color: '#9ca3af',
                  marginTop: '4px',
                  marginBottom: '0'
                }}>
                  Auto-filled with current page URL
                </p>
              </div>

              {/* Message Field */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  Message
                </label>
                <textarea
                  value={feedbackForm.content}
                  onChange={(e) => handleFeedbackInputChange('content', e.target.value)}
                  placeholder="Please describe your feedback, bug report, or suggestion in detail..."
                  rows={6}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '24px',
              paddingTop: '16px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                onClick={closeFeedbackModal}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  color: '#374151'
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitFeedback}
                disabled={isSubmittingFeedback}
                style={{
                  padding: '8px 16px',
                  backgroundColor: isSubmittingFeedback ? '#9ca3af' : '#22c55e',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: isSubmittingFeedback ? 'not-allowed' : 'pointer',
                  color: 'white',
                  fontWeight: '500',
                  opacity: isSubmittingFeedback ? 0.7 : 1
                }}
              >
                {isSubmittingFeedback ? 'Sending...' : 'Send Feedback'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DESKTOP LAYOUT */}
      <div className="desktop-layout" style={{
        display: 'flex',
        width: '100%',
        minHeight: '100vh'
      }}>
        {/* DESKTOP SIDEBAR */}
        <div style={{
          width: '250px',
          backgroundColor: '#243665',
          color: 'white',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh'
        }}>
          {/* Logo Section */}
          <div style={{
            width: '100%',
            padding: '16px',
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer'
          }}
          onClick={handleHomeClick}
          >
            <img 
              src={logo}
              alt="Pitch Allocator"
              style={{
                width: '100%',
                maxWidth: '218px',
                height: 'auto',
                objectFit: 'contain'
              }}
            />
          </div>
          
          {/* Branded Club Info Card */}
          <div style={{ padding: '12px' }}>
            <div style={{
              background: `linear-gradient(135deg, ${branding?.primaryColor || '#10b981'} 0%, ${branding?.secondaryColor || '#3b82f6'} 100%)`,
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            }}>
              {/* Club Logo + Name Row */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '6px'
              }}>
                {branding?.clubLogoUrl && (
                  <img 
                    src={branding.clubLogoUrl} 
                    alt="Club Logo" 
                    style={{
                      height: '58px',
                      width: '58px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      flexShrink: 0
                    }}
                  />
                )}
                <div style={{
                  fontSize: '17px',
                  fontWeight: '600',
                  color: 'white',
                  lineHeight: '1.3'
                }}>
                  {clubName || 'Loading...'}
                </div>
              </div>
              <div style={{
                fontSize: '13px',
                color: 'rgba(255,255,255,0.7)',
                marginBottom: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {user?.email || 'Not logged in'}
              </div>
              <div style={{
                fontSize: '11px',
                color: 'white',
                padding: '4px 10px',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '20px',
                display: 'inline-block',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {userRole === 'admin' ? 'Administrator' : userRole === 'viewer' ? 'Viewer' : 'User'}
              </div>
            </div>
          </div>
          
          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* Bottom navigation buttons - Matches Menu.jsx styling */}
          <div style={{
            padding: '12px',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            backgroundColor: 'rgba(0,0,0,0.1)',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px'
          }}>
            <button
              onClick={handleHomeClick}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.9)',
                cursor: 'pointer',
                fontWeight: '500',
                transition: 'all 0.15s ease',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
              }}
            >
              Home Menu
            </button>
            
            <button
              onClick={() => navigate('/capacity-outlook')}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.9)',
                cursor: 'pointer',
                fontWeight: '500',
                transition: 'all 0.15s ease',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
              }}
            >
              Reporting
            </button>
            
            <AdminOnly>
              <button
                onClick={() => navigate('/settings')}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: 'rgba(255,255,255,0.9)',
                  cursor: 'pointer',
                  fontWeight: '500',
                  transition: 'all 0.15s ease',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                }}
              >
                Settings
              </button>
            </AdminOnly>
            
            <button
              onClick={openFeedbackModal}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.9)',
                cursor: 'pointer',
                fontWeight: '500',
                transition: 'all 0.15s ease',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
              }}
            >
              Feedback
            </button>
            
            <button
              onClick={handleLogout}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.9)',
                cursor: 'pointer',
                fontWeight: '500',
                transition: 'all 0.15s ease',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
              }}
            >
              Logout
            </button>
          </div>
        </div>

        {/* DESKTOP MAIN CONTENT */}
        <div style={{
          flex: 1,
          padding: clubData?.subscriptionStatus === 'trial' ? '80px 20px 12px' : '8px 20px 12px',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '6px' }}>
            <h1 style={{ 
              color: '#1f2937', 
              marginBottom: '0',
              fontWeight: 'bold',
              fontSize: '28px'
            }}>
              {clubName}
            </h1>
          </div>

          {/* Facility Selector Container - Same width as map */}
          {facilities.length >= 2 && (
            <div style={{
              maxWidth: '1200px',
              margin: '0 auto 6px',
              width: '100%',
              backgroundColor: 'white',
              borderRadius: '10px',
              padding: '10px 16px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <div style={{
                  fontSize: '10px',
                  fontWeight: '600',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '6px'
                }}>
                  Select a Location
                </div>
                <div style={{
                  display: 'flex',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '9999px',
                  padding: '3px',
                  gap: '3px'
                }}>
                  {facilities.map((facility) => (
                    <button
                      key={facility.id}
                      onClick={() => {
                        setSelectedFacilityId(facility.id);
                        setImageLoaded(false);
                        console.log('🔄 [ClubPitchMap] Switched to facility:', facility.id);
                      }}
                      style={{
                        padding: '7px 16px',
                        fontSize: '13px',
                        fontWeight: '600',
                        border: 'none',
                        borderRadius: '9999px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        backgroundColor: selectedFacilityId === facility.id ? '#1e3a5f' : 'transparent',
                        color: selectedFacilityId === facility.id ? 'white' : '#374151',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {facility.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Map Container */}
          <div style={{
            display: 'flex',
            gap: '12px',
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%'
          }}>
            {/* CANVAS - no extra wrapper, just the map */}
            <div style={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start'
            }}>
              {canvasSize.width > 0 && canvasSize.height > 0 ? (
                <canvas
                  ref={canvasRef}
                  width={canvasSize.width}
                  height={canvasSize.height}
                  style={{ 
                    display: 'block',
                    maxWidth: '100%',
                    height: 'auto',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                    border: '1px solid #d1d5db'
                  }}
                  onClick={handleCanvasClick}
                />
              ) : (
                <div style={{
                  width: '100%',
                  maxWidth: '900px',
                  aspectRatio: '4/3',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px'
                }}>
                  <div style={{ textAlign: 'center', color: '#6b7280' }}>
                    Loading map...
                  </div>
                </div>
              )}
              
              {/* HIDDEN IMAGE - CORS FIXED */}
              <img
                ref={imageRef}
                src={satelliteConfig.imageUrl}
                onLoad={handleImageLoad}
                onError={handleImageError}
                style={{ display: 'none' }}
                alt="Satellite view"
              />
            </div>

            {/* LEGEND */}
            {satelliteConfig?.pitchBoundaries?.length > 0 && (
              <div style={{
                width: '160px',
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '10px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                border: '1px solid #e5e7eb',
                height: 'fit-content',
                flexShrink: 0
              }}>
                <h4 style={{  fontWeight: 'bold', margin: '0 0 8px 0', color: '#374151', fontSize: '13px' }}>Pitch Legend</h4>
                {satelliteConfig.pitchBoundaries.map((pitch, index) => {
                  const pitchNumber = pitch.pitchNumber || (index + 1);
                  
                  // Check multiple key formats (same as getPitchDisplayName)
                  const possibleKeys = [
                    `pitch-${pitchNumber}`,
                    pitch.id,
                    `pitch${pitchNumber}`,
                    `Pitch ${pitchNumber}`,
                    `Pitch-${pitchNumber}`,
                    pitchNumber.toString(),
                    pitchNumber
                  ].filter(Boolean);
                  
                  // PRIORITY 1: Check pitchNames for custom name (try all possible keys)
                  let displayName = null;
                  for (const key of possibleKeys) {
                    if (pitchNames && pitchNames[key] && pitchNames[key].trim && pitchNames[key].trim() !== '') {
                      displayName = pitchNames[key];
                      break;
                    }
                  }
                  
                  // PRIORITY 2: Use pitch.name from facility document
                  if (!displayName && pitch.name && pitch.name.trim() !== '') {
                    displayName = pitch.name;
                  }
                  
                  // PRIORITY 3: Fallback to "Pitch X"
                  if (!displayName) {
                    displayName = `Pitch ${pitchNumber}`;
                  }
                  
                  return (
                    <div key={index} style={{
                      padding: '4px 8px',
                      backgroundColor: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      borderRadius: '4px',
                      marginBottom: '4px',
                      fontSize: '12px',
                      color: '#166534',
                      fontWeight: '500'
                    }}>
                      {displayName}
                    </div>
                  );
                })}
                
                <div style={{
                  marginTop: '10px',
                  padding: '6px',
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: '4px',
                  fontSize: '10px',
                  color: '#1e40af',
                  textAlign: 'center',
                  lineHeight: '1.4'
                }}>
                  Click a pitch to make allocations
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* MOBILE LAYOUT */}
      <div className="mobile-layout" style={{
        display: 'none',
        flexDirection: 'column',
        width: '100%',
        minHeight: '100vh',
        backgroundColor: '#f9fafb'
      }}>
        {/* Mobile Header with Burger Menu */}
        <div style={{
          backgroundColor: '#243665',
          color: 'white',
          padding: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          position: 'relative'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img 
              src={logo}
              alt="Pitch Allocator"
              style={{
                height: '32px',
                width: 'auto',
                objectFit: 'contain',
                cursor: 'pointer'
              }}
              onClick={handleHomeClick}
            />
            <div>
              <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                {clubName || 'Loading...'}
              </div>
              <div style={{ fontSize: '11px', opacity: 0.8 }}>
                Select a pitch to begin
              </div>
            </div>
          </div>
          
          <button
            onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '8px',
              minWidth: '44px',
              minHeight: '44px'
            }}
          >
            ☰
          </button>
        </div>

        {/* Mobile Sidebar Overlay */}
        {isMobileSidebarOpen && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999
            }}
            onClick={() => setIsMobileSidebarOpen(false)}
          />
        )}

        {/* Mobile Sidebar */}
        <div style={{
          position: 'fixed',
          top: 0,
          right: isMobileSidebarOpen ? '0' : '-300px',
          width: '280px',
          height: '100vh',
          backgroundColor: '#243665',
          transition: 'right 0.3s ease',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto'
        }}>
          {/* Mobile Sidebar Header */}
          <div style={{
            padding: '20px 16px',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            backgroundColor: 'rgba(0,0,0,0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'white' }}>
                Menu
              </div>
              <button
                onClick={() => setIsMobileSidebarOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                ✕
              </button>
            </div>
          </div>

          {/* Branded Club Info Card - Mobile */}
          <div style={{ padding: '12px' }}>
            <div style={{
              background: `linear-gradient(135deg, ${branding?.primaryColor || '#10b981'} 0%, ${branding?.secondaryColor || '#3b82f6'} 100%)`,
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            }}>
              {/* Club Logo + Name Row */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '6px'
              }}>
                {branding?.clubLogoUrl && (
                  <img 
                    src={branding.clubLogoUrl} 
                    alt="Club Logo" 
                    style={{
                      height: '58px',
                      width: '58px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      flexShrink: 0
                    }}
                  />
                )}
                <div style={{
                  fontSize: '17px',
                  fontWeight: '600',
                  color: 'white',
                  lineHeight: '1.3'
                }}>
                  {clubName || 'Loading...'}
                </div>
              </div>
              <div style={{
                fontSize: '13px',
                color: 'rgba(255,255,255,0.7)',
                marginBottom: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {user?.email || 'Not logged in'}
              </div>
              <div style={{
                fontSize: '11px',
                color: 'white',
                padding: '4px 10px',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '20px',
                display: 'inline-block',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {userRole === 'admin' ? 'Administrator' : userRole === 'viewer' ? 'Viewer' : 'User'}
              </div>
            </div>
          </div>

          {/* Mobile Navigation Buttons - Matches Menu.jsx styling */}
          <div style={{ 
            padding: '12px',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '6px'
          }}>
            <button
              onClick={handleHomeClick}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.9)',
                cursor: 'pointer',
                fontWeight: '500',
                textAlign: 'left',
                minHeight: '44px'
              }}
            >
              Home Menu
            </button>

            <button
              onClick={() => handleNavigation('/capacity-outlook')}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.9)',
                cursor: 'pointer',
                fontWeight: '500',
                textAlign: 'left',
                minHeight: '44px'
              }}
            >
              Reporting
            </button>

            <button
              onClick={() => handleNavigation('/settings')}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.9)',
                cursor: 'pointer',
                fontWeight: '500',
                textAlign: 'left',
                minHeight: '44px'
              }}
            >
              Settings
            </button>

            <button
              onClick={openFeedbackModal}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.9)',
                cursor: 'pointer',
                fontWeight: '500',
                textAlign: 'left',
                minHeight: '44px'
              }}
            >
              Feedback
            </button>

            <div style={{ flex: 1, minHeight: '20px' }} />

            <button
              onClick={handleLogout}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.9)',
                cursor: 'pointer',
                fontWeight: '500',
                textAlign: 'left',
                minHeight: '44px'
              }}
            >
              Logout
            </button>
          </div>
        </div>

        {/* Mobile Pitch Buttons */}
        <div style={{
          flex: 1,
          padding: clubData?.subscriptionStatus === 'trial' ? '70px 20px 20px' : '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          maxWidth: '600px',
          margin: '0 auto',
          width: '100%'
        }}>
          {/* Mobile Facility Selector - Pill buttons - Only show if 2+ facilities */}
          {facilities.length >= 2 && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '16px',
              backgroundColor: '#f9fafb',
              borderRadius: '12px',
              marginBottom: '12px'
            }}>
              <div style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '10px'
              }}>
                Select a Location
              </div>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                backgroundColor: '#e5e7eb',
                borderRadius: '9999px',
                padding: '4px',
                gap: '4px'
              }}>
                {facilities.map((facility) => (
                  <button
                    key={facility.id}
                    onClick={() => {
                      setSelectedFacilityId(facility.id);
                      setImageLoaded(false);
                      console.log('🔄 [ClubPitchMap] Mobile switched to facility:', facility.id);
                    }}
                    style={{
                      padding: '10px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      border: 'none',
                      borderRadius: '9999px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: selectedFacilityId === facility.id ? '#1e3a5f' : 'transparent',
                      color: selectedFacilityId === facility.id ? 'white' : '#374151',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {facility.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {satelliteConfig?.pitchBoundaries?.map((pitch, index) => {
            const pitchNumber = pitch.pitchNumber || (index + 1);
            
            // Check multiple key formats (same as getPitchDisplayName)
            const possibleKeys = [
              `pitch-${pitchNumber}`,
              pitch.id,
              `pitch${pitchNumber}`,
              `Pitch ${pitchNumber}`,
              `Pitch-${pitchNumber}`,
              pitchNumber.toString(),
              pitchNumber
            ].filter(Boolean);
            
            // PRIORITY 1: Check pitchNames for custom name (try all possible keys)
            let displayName = null;
            for (const key of possibleKeys) {
              if (pitchNames && pitchNames[key] && pitchNames[key].trim && pitchNames[key].trim() !== '') {
                displayName = pitchNames[key];
                break;
              }
            }
            
            // PRIORITY 2: Use pitch.name from facility document
            if (!displayName && pitch.name && pitch.name.trim() !== '') {
              displayName = pitch.name;
            }
            
            // PRIORITY 3: Fallback to "Pitch X"
            if (!displayName) {
              displayName = `Pitch ${pitchNumber}`;
            }
            
            return (
              <button
                key={index}
                onClick={() => {
                  if (onPitchClick) {
                    onPitchClick(pitch);
                  } else {
                    const pitchId = pitch.pitchNumber || 'pitch1';
                    navigate(`/allocator/${pitchId}`);
                  }
                }}
                style={{
                  padding: '20px',
                  backgroundColor: 'white',
                  border: '2px solid #22c55e',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.2s ease',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px'
                }}
              >
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#22c55e',
                  color: 'white',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '20px'
                }}>
                  {pitchNumber}
                </div>
                <div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#1f2937',
                    marginBottom: '4px'
                  }}>
                    {displayName}
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#6b7280'
                  }}>
                    Tap to manage allocations
                    {pitch.type === 'rotated' && (
                      <span style={{ 
                        fontSize: '12px', 
                        color: '#3b82f6', 
                        marginLeft: '8px',
                        fontStyle: 'italic'
                      }}>
                        (Diagonal pitch)
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}

          {(!satelliteConfig?.pitchBoundaries || satelliteConfig.pitchBoundaries.length === 0) && (
            <div style={{
              backgroundColor: '#fff3cd',
              border: '2px solid #fbbf24',
              borderRadius: '12px',
              padding: '20px',
              textAlign: 'center',
              margin: '20px'
            }}>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#92400e', marginBottom: '8px' }}>
                No Pitches Configured
              </div>
              <div style={{ color: '#92400e' }}>
                Please set up pitch boundaries in Settings to continue.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClubPitchMap;
